import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  HostListener,
  inject,
  input,
  OnInit,
  signal,
  viewChildren,
} from '@angular/core';
import { contentLayout7Style } from '@components/content/components/content-layout-7/config';
import { ContentLayout7, ContentLayout7Style } from '@components/content/components/content-layout-7/model';
import { ResponsiveService } from '@services/responsive';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { ColorPipe } from '@shared/pipes';

@Component({
  selector: 'app-content-layout-7',
  imports: [TitleComponent, ColorPipe],
  templateUrl: './content-layout-7.component.html',
  styleUrl: './content-layout-7.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentLayout7Component implements OnInit {
  readonly data = input.required<ContentLayout7>();
  readonly paragraphElements = viewChildren('paragraphElements', { read: ElementRef });
  activeTitle = signal(0);
  title = signal<Title>(null!);
  items = signal<{ title: Title; text: string }[]>(null!);
  style = signal<ContentLayout7Style>(null!);
  breakpoints = inject(ResponsiveService).breakpoints;
  readonly #document = inject<Document>(DOCUMENT);
  readonly #window = this.#document.defaultView as Window;
  private offsetGap = signal(260);

  ngOnInit(): void {
    this.style.set(contentLayout7Style[this.data().theme ?? 'BLUE']!);
    this.title.set({
      level: 'SPAN',
      type: 'SLASH',
      color: this.style().labelColor,
      text: this.data().title,
      align: {
        mobile: 'LEFT',
        desktop: 'LEFT',
      },
      weight: 500,
      size: {
        mobile: 'SPAN',
        desktop: 'SPAN',
      },
    });
    this.items.set(
      this.data().items.map((item) => ({
        ...item,
        title: {
          level: 'SPAN',
          size: {
            mobile: 'H3',
            desktop: 'H3',
          },
          color: this.style().textColor,
          align: {
            mobile: 'LEFT',
            desktop: 'LEFT',
          },
          text: item.title,
        },
      }))
    );
  }

  @HostListener('window:scroll') onScrollEvent() {
    const paragraphElements = this.paragraphElements();
    if (paragraphElements && paragraphElements.length && this.breakpoints() === 'desktop') {
      paragraphElements.forEach((paragraphElement, i) => {
        const el = paragraphElement.nativeElement as HTMLElement;
        if (
          this.#window.scrollY >= el.offsetTop - this.offsetGap() &&
          this.#window.scrollY <= el.offsetTop + el.offsetHeight
        ) {
          this.activeTitle.set(i);
        }
      });
    }
  }

  goToSection(anchor: string) {
    this.#document.getElementById(anchor)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  }
}
